<template lang="pug">

    article.article-page(:class="`${realActive ? 'active' : ''} article-page--${id}`")

        .article-page__vp(aria-hidden="true", id="vp-start")
        .article-page__sections(ref="wrap")

            app-section(v-for="section, index in sections", :key="index", :settings="settings", :content="section", :index="index", :data-index="index + 1", :data-label="section.title", :id="`s-${id}-${index}`", :initiated="index <= lastIndex", :active="realActive && (index === curIndex || index === curIndex - 1)")


        // div {{realContent}}


</template>

<script>
/* eslint-disable no-console */

import {mapGetters} from 'vuex';
import * as basicScroll from 'basicscroll';
import {TweenLite, TweenMax, Sine} from 'gsap';

/* meta info (for title) */
import meta from '@/assets/data/meta';

import SizesUtils from '../assets/js/viscom/viscomjs/utils/SizesUtils';

/* Components */
import AppSection from '@/components/AppSection.vue';

export default {
    name: 'article-page',

    metaInfo() {
        return {
            title: this.metaTitle,
            meta: [
                {vmid: 'description', name: 'description', content: this.meta.description},
                {vmid: 'keywords', name: 'keywords', content: this.meta.keywords || meta.keywords || ''},
                {vmid: 'og:title', property: 'og:title', content: this.metaTitle},
                {vmid: 'og:description', property: 'og:description', content: this.meta.description},
                {vmid: 'og:image', property: 'og:image', content: `${this.metaUrl}img/${this.meta.share}`},
                {vmid: 'twitter:title', name: 'twitter:title', content: this.metaTitle},
                {vmid: 'twitter:description', name: 'twitter:description', content: this.meta.description},
                {vmid: 'twitter:image', name: 'twitter:image', content: `${this.metaUrl}img/${this.meta.share}`},
                {vmid: 'apple-mobile-web-app-title', property: 'apple-mobile-web-app-title', content: this.metaTitle},
                {vmid: 'application-name', property: 'application-name', content: this.metaTitle}
            ]
        };
    },

    props: {
        settings: Object,
        id: {
            default: '',
            type: String
        }
    },

    components: {
        AppSection
    },

    data() {
        return {
            leave: false,
            wait: false,
            active: false,
            visible: false,
            noScroll: false,
            content: null,
            realContent: {},
            contentKey: '',
            curIndex: 0,
            lastIndex: 1
        };
    },

    computed: {
        ...mapGetters('nav', {
            navPageConfig: 'currentData',
            newSection: 'newSection'
        }),

        ...mapGetters('core', ['isLiteMode']),

        title() {
            return this.content ? this.content.title || '' : '';
        },

        metaTitle() {
            return this.title.replace(/&nbsp;/g, ' ');
        },

        meta() {
            return this.realContent.meta || meta;
        },

        metaUrl() {
            return meta.url;
        },

        sections() {
            return this.realContent.sections || [];
        },

        maxIndex() {
            return this.sections.length + 1;
        },

        realActive() {
            return this.active && this.visible && !this.wait;
        }
    },

    beforeRouteEnter(to, from, next) {
        next(vm => {
            const key = vm.$props.settings.contentKey || to.name;
            vm.wait = true;
            vm.$store.dispatch('routes/getContent', {name: key, params: to.params}).then(
                //
                data => {
                    if (!data) {
                        next(false);
                        vm.$router.push('/');
                        return;
                    }

                    vm.killDelayedCalls();

                    vm.leave = false;
                    vm.content = data;
                    vm.contentKey = key;

                    vm.show();

                    next();
                },
                () => {
                    next(false);
                }
            );
        });
    },

    beforeRouteUpdate(to, from, next) {
        const key = this.$props.settings.contentKey || to.name;
        this.wait = true;
        this.$store.dispatch('routes/getContent', {name: key, params: to.params}).then(
            //
            data => {
                if (!data) {
                    next(false);
                    this.$router.push('/');
                    return;
                }

                this.killDelayedCalls();

                this.leave = false;
                this.content = data;
                this.contentKey = key;

                this.show();

                next();
            },
            () => {
                next(false);
            }
        );
    },

    beforeRouteLeave(to, from, next) {
        this.leave = true;
        this.active = false;
        this.wait = false;

        next();
    },

    mounted() {
        this.y = 0;
        this.isAtFirstScreen = true;
        this.transitionToFromFirstScreen = false;

        if (this.content) {
            this.visible = false;
            this.lastIndex = 1;
            this.realContent = this.content;

            this._updateSectionsData();
        }

        // init scripts
    },

    beforeDestroy() {
        this.killDelayedCalls();

        // deinit scripts
    },

    watch: {
        content() {
            this.lastIndex = 1;
            this.visible = false;
            // this.goUp();
            this.stopBasicscroll();

            // this.ready();
            TweenLite.delayedCall(0.15, this.ready, [], this);
            TweenLite.delayedCall(1.5, this._updateAndShow, [], this);
            // TODO: show arrow to goDown
        },

        sections() {
            this.$nextTick(function() {
                this.visible = false;
                this.setObject3d();
                TweenLite.delayedCall(0.15, this._updateSectionsData, [], this);
            });
        },

        lastIndex(to, from) {
            if (to === from) return;

            this.basicScrollUpdate();
            TweenLite.delayedCall(0.15, this.basicScrollUpdate, [], this);
        },

        newSection(to) {
            this.scrollToElement(to, 0.5);

            this.$store.dispatch('nav/resetNewSection');
        }
    },

    methods: {
        scrollToElement(id, t = 0) {
            const el = document.getElementById(id);
            if (!el) return;

            TweenMax.killDelayedCallsTo(this._scrollToElement);

            const newIndex = parseInt(el.dataset.index);
            let d = 0;
            if (newIndex > this.lastIndex) {
                this.lastIndex = newIndex;
                d = 0.2;
            }

            TweenMax.delayedCall(d, this._scrollToElement, [id, t]);
        },

        _scrollToElement(id, t) {
            const el = document.getElementById(id);
            if (!el) return;

            this.basicScrollUpdate();
            TweenLite.delayedCall(0.15, this.basicScrollUpdate, [], this);

            TweenMax.to(window, t, {
                scrollTo: {y: '#' + id, offsetY: 70, autoKill: false},
                ease: Sine.easeInOut,
                onComplete: function() {
                    this.basicScrollUpdate();

                    const rect = el.getBoundingClientRect();
                    if (rect.top >= -10 && rect.top < window.innerHeight * 0.5) this._setVisible();
                    else this._scrollToElement(id, Math.max(0.1, t));
                },
                onCompleteScope: this
            });
        },

        ready() {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    this.$store.dispatch('core/ready');
                    this._setupDecorState();
                });
            });
        },

        killDelayedCalls() {
            TweenLite.killDelayedCallsTo(this._show);
            TweenLite.killDelayedCallsTo(this._updateAndShow);
            TweenLite.killDelayedCallsTo(this._updateSectionsData);
            TweenLite.killDelayedCallsTo(this._updateObjectsList);
            TweenLite.killDelayedCallsTo(this._setVisible);
            TweenLite.killDelayedCallsTo(this.startBasicscroll);
            TweenLite.killDelayedCallsTo(this.basicScrollUpdate);
            TweenLite.killDelayedCallsTo(this._scrollToElement);
            TweenLite.killDelayedCallsTo(this.addLastIndex);
        },

        show() {
            TweenLite.delayedCall(0.15, this._show, [], this);
        },

        _show() {
            this.active = true;
        },

        _setVisible() {
            this.visible = true;

            this.basicScrollUpdate();
            TweenLite.delayedCall(0.15, this.basicScrollUpdate, [], this);

            this._updateObjectsList();
            TweenLite.delayedCall(0.15, this._updateObjectsList, [], this);
        },

        _updateSectionsData() {
            this._getObjectsList();
            this._updateObjectsList();
            this.wait = false;

            if (this.newSection) {
                TweenLite.delayedCall(0.15, this._updateScroll, this);
            } else {
                TweenLite.delayedCall(0.15, this._setVisible, [], this);
            }
        },

        _updateScroll() {
            if (this.newSection) {
                this.scrollToElement(this.newSection);
                this.$store.dispatch('nav/resetNewSection');
            }
        },

        _updateObjectsList() {
            TweenLite.killDelayedCallsTo(this._updateObjectsList);
            this.$store.dispatch('decor3d/setObjectsPosition', this.getObjectsBoxes());
            window.dispatchEvent(new CustomEvent('updateObjectsList'));
        },

        _setupDecorState() {
            this.$store.dispatch('decor3d/setState', 'page');
        },

        _updateAndShow() {
            this.realContent = this.content ? this.content : {};

            TweenLite.delayedCall(0.15, this.startBasicscroll, [], this);
        },

        goUp() {
            TweenMax.killTweensOf(window);

            this.transitionToFromFirstScreen = true;

            TweenMax.to(window, 0.3, {
                scrollTo: {y: 0, autoKill: false},
                onComplete: () => {
                    this.isAtFirstScreen = true;
                    this.transitionToFromFirstScreen = false;
                }
            });
        },

        goDown() {
            if (this.noScroll) return;
            TweenMax.killTweensOf(window);

            this.transitionToFromFirstScreen = true;

            TweenMax.to(window, 0.65, {
                scrollTo: {y: this.$refs.wrap, autoKill: false},
                onComplete: () => {
                    this.isAtFirstScreen = false;
                    this.transitionToFromFirstScreen = false;
                }
            });
        },

        // 3D
        setObject3d() {
            this.$store.dispatch('decor3d/setupObjectsList', this.content.objects);
            this.$store.dispatch('decor3d/setObjectsProgress', new Array(this.content.objects ? this.content.objects.length : 0).fill(0));
        },

        getObjectsBoxes() {
            if (!this._objList || this._objList.length < 1) return [];

            return this._objList.map(el => {
                const rect = el.getBoundingClientRect();
                return {x: rect.left, y: rect.top + SizesUtils.getWinScrollTop()};
            });
        },

        _getObjectsList() {
            this._objList = [...document.querySelectorAll('.object-three__point')];
        },

        // BASICSCROLL

        setProgress(index, p) {
            if (!((p > 0 || index === 0) && p <= 1)) return;

            this.setLastIndex(index); // - 1 + 1
            this.setCurIndex(index);

            this.$store.dispatch('nav/setCurrentSectionProgress', {index: index, progress: p});

            TweenLite.delayedCall(5, this.addLastIndex, this);
        },

        setCurIndex(index) {
            if (this.curIndex === index) return;
            this.curIndex = index;
            this.currentSectionID = `s-${this.id}-${index - 1}`;

            const el = document.getElementById(this.currentSectionID);
            if (!el) return;

            this.trackSectionActive(el.dataset.label, `s-${this.id}-${index - 1}`);

            this._updateObjectsList();
        },

        setLastIndex(index) {
            TweenLite.killDelayedCallsTo(this.addLastIndex);

            if (index > this.lastIndex) {
                this.lastIndex = index;

                this._getObjectsList();
                this.$nextTick(() => {
                    TweenLite.delayedCall(0.15, this._getObjectsList, this);
                });
            }
        },

        addLastIndex() {
            if (this.lastIndex >= this.maxIndex) return;
            this.setLastIndex(this.lastIndex + 1);

            TweenLite.delayedCall(5, this.addLastIndex, this);
        },

        startBasicscroll() {
            if (this._hasBSC) return;

            const elems = [...this.$el.querySelectorAll('.app-section')];
            elems.unshift(this.$el.querySelector('.article-page__vp'));


            this._BSCs = elems.map((el, index) => {
                const update = (instance, percentage) => {
                    this.setProgress(index, percentage / 100);
                };
                const instance = basicScroll.create({
                    elem: el,
                    from: 'top-top',
                    to: 'bottom-top',
                    direct: true,
                    props: {
                        '--shift': {
                            from: 0,
                            to: 1
                        }
                    },
                    inside: update,
                    outside: update
                });
                instance.start();
                return instance;
            });

            this.bsupdated = true;

            TweenLite.delayedCall(0.1, this.basicScrollUpdate, [], this);

            this._hasBSC = true;
        },

        stopBasicscroll() {
            if (!this._hasBSC) return;

            window.removeEventListener('scroll', this._basicScrollUpdate);
            TweenLite.killDelayedCallsTo(this.basicScrollUpdate);
            this._BSCs.forEach(instance => {
                instance.destroy();
            });

            this._hasBSC = false;
            this._BSCs.splice(0, this._BSCs.length);
        },

        basicScrollUpdate() {
            if (!this.bsupdated) return;
            this.bsupdated = false;
            this._basicScrollUpdate();
            this.bsupdated = false;

            window.addEventListener('scroll', this._basicScrollUpdate);
        },

        _basicScrollUpdate() {
            if (this.bsupdated) return;
            if (!this._BSCs) return;
            this._BSCs.forEach(instance => {
                instance.update();
                instance.calculate();
            });
            window.removeEventListener('scroll', this._basicScrollUpdate);
            this.bsupdated = true;
        },

        trackSectionActive(label, id) {
            if (typeof label === 'undefined') return;
            if (this.$analytics !== undefined) {
                /*this.$analytics.sendEvent({
                    event: 'eventTracking',
                    category: `Navigation`,
                    action: 'scroll',
                    label: `Page: ${this.title}, Section: ${label}`
                });*/
                this.$analytics.trackView(`Page: ${this.title}, Section: ${label}`, `${this.$router.currentRoute.path}#${id}`);
            }
        }
    }
};
</script>

<style lang="scss">
@import '../assets/css/vars.scss';

.article-page {
    width: 100%;
    // min-height: calc(99.9vh - var(--scr-padding-bottom) - var(--scr-padding-top));
    transition: opacity 0.15s ease;
    pointer-events: none;

    &__vp {
        display: block;
        height: 100vh;
        background: var(--color-main-bg);
        opacity: calc(1.5 * var(--shift));
    }

    &:not(.active) {
        opacity: 0;
        transition-duration: 0.1s;
    }

    &__hidden-h1 {
        font-size: 0;
        line-height: 0;
        position: absolute;
        width: 1px;
        height: 1px;
        left: -100px;
        top: -100px;
    }

    &__title {
        display: flex;
        min-height: calc(1.35em * 3);
        align-items: flex-end;

        @media (--viewport-mobile), (--viewport-tablet-pt) {
            min-height: 0;
        }
    }

    &__sections {
        width: 100%;
        overflow: hidden;
        pointer-events: all;

        &.noScroll {
            display: none;
        }
    }
}
</style>
